import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';

import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/auth/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { ReportData } from '@/types/report';

import {
  buildEditDiff,
  recomputeDerivedIfNeeded,
  fillBeforeForDerivedKeys,
  buildDevelopmentReportJson,
  aggregatePendingValues,
  mergeWithPending,
  findSupersededPendingIds,
  findOverlappingPendingIds,
  type ReportChangeLog,
} from '@/utils/reportEditUtils';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { StudentEditField } from '@/components/admin/edit-report/StudentEditField';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';

// ---------- Options (ดึงจาก src/config/formConfig/page2.ts) ----------
const LOCATION_OPTIONS = [
  { value: 'online', label: 'Online' },
  { value: 'siam', label: 'สาขาสยาม' },
  { value: 'ngamwongwan', label: 'สาขางามวงศ์วาน' },
];

const TEACHING_TYPE_OPTIONS = [
  { value: 'Private มีชีท = คุณครูใช้ชีทตัวเอง', label: 'Private มีชีท (ครูใช้ชีทตัวเอง)' },
  { value: 'Private ไม่มีชีท = คุณครูใช้ชีทของสถาบัน (ชีทภาษาไทย)', label: 'Private ไม่มีชีท (ใช้ชีทสถาบัน TH)' },
  { value: 'Premier_TH ไม่มีชีท = คุณครูใช้ชีทของสถาบัน', label: 'Premier_TH ไม่มีชีท (ใช้ชีทสถาบัน)' },
  { value: 'Premier_TH มีชีท = คุณครูใช้ชีทตัวเอง/ชีทน้อง (ชีทภาษาไทย)', label: 'Premier_TH มีชีท (TH)' },
  { value: 'Premier_Inter ไม่มีชีท = คุณครูใช้ชีทของสถาบัน (ชีทภาษาอังกฤษ)', label: 'Premier_Inter ไม่มีชีท (EN)' },
  { value: 'Premier_Inter มีชีท = คุณครูใช้ชีทตัวเอง/ชีทน้อง (ชีทภาษาอังกฤษ)', label: 'Premier_Inter มีชีท (EN)' },
];

const DURATION_OPTIONS = [
  { value: '30 นาที', label: '30 นาที' },
  { value: '1 ชม.', label: '1 ชม.' },
  { value: '1.30 ชม.', label: '1 ชม. 30 นาที' },
  { value: '2 ชม.', label: '2 ชม.' },
  { value: '2.30 ชม.', label: '2 ชม. 30 นาที' },
  { value: '3 ชม.', label: '3 ชม.' },
  { value: '4 ชม.', label: '4 ชม.' },
];

const GRADE_OPTIONS = [
  'ป.1', 'ป.2', 'ป.3', 'ป.4', 'ป.5', 'ป.6',
  'ม.1', 'ม.2', 'ม.3', 'ม.4', 'ม.5', 'ม.6',
];

const SUBJECT_OPTIONS = [
  'วิทยาศาสตร์', 'คณิตศาสตร์', 'ฟิสิกส์', 'เคมี', 'ชีวะ',
  'สังคม', 'ภาษาไทย', 'ภาษาอังกฤษ', 'ภาษาจีน', 'ประวัติศาสตร์',
];

const LEARNING_STATUS_OPTIONS = [
  { value: 'เข้าเรียน/เข้าสอนปกติ', label: 'เข้าเรียน / เข้าสอนปกติ' },
  { value: 'น้องไม่เข้าเรียน (ลากะทันหัน)', label: 'น้องไม่เข้าเรียน (ลากะทันหัน)' },
];

const STATUS_OPTIONS = [
  { value: 'WAITING', label: 'รอการตรวจสอบ' },
  { value: 'APPROVED', label: 'อนุมัติแล้ว' },
  { value: 'REJECTED', label: 'ไม่อนุมัติ' },
];

// ---------- Development report sub-fields (ตาม /form page3.ts) ----------
const RATING_OPTIONS = [
  { value: '5', label: '5 (ดีมาก)' },
  { value: '4', label: '4 (ดี)' },
  { value: '3', label: '3 (พอใช้)' },
  { value: '2', label: '2 (ควรปรับปรุง)' },
  { value: '1', label: '1 (ต้องปรับปรุงมาก)' },
];

const PROBLEM_OPTIONS = [
  'ตีความโจทย์ผิด / ไม่เข้าใจคำถาม',
  'คำนวณผิดพลาดในขั้นตอนพื้นฐาน',
  'ขาดความมั่นใจในการตอบ',
  'เร่งรีบ ไม่ตรวจสอบคำตอบ',
  'ฟังโจทย์ไม่ละเอียด อ่านจับใจความผิดพลาด',
  'สับสนไวยากรณ์หรือโครงสร้างภาษา',
  'สมาธิสั้น มีสิ่งรบกวนระหว่างเรียน',
];

const SOLUTION_OPTIONS = [
  'อธิบายซ้ำโดยใช้วิธีอื่น',
  'ให้ทำแบบฝึกหัดเพิ่มเติม',
  'ใช้คำถามปลายเปิด',
  'ให้ทำโจทย์แบบมีลำดับขั้น',
  'อธิบายความเชื่อมโยงกับวิชาอื่น',
];

const ABSENT_STATUS = 'น้องไม่เข้าเรียน (ลากะทันหัน)';
const ABSENT_DEV_REPORT = 'นักเรียนไม่เข้าเรียน';

type DevReportJson = {
  topic_learned?: string;
  knowledge_rating?: string;
  knowledge_behavior?: string;
  skills_rating?: string;
  skills_behavior?: string;
  attitude_rating?: string;
  attitude_behavior?: string;
  student_improvement?: string;
  problems_encountered?: string[];
  problems_encountered_other?: string;
  solutions_applied?: string[];
  solutions_applied_other?: string;
};

const parseDevReport = (
  raw?: string
): { data: DevReportJson; isAbsent: boolean; isMalformed: boolean } => {
  if (!raw) return { data: {}, isAbsent: false, isMalformed: false };
  if (raw === ABSENT_DEV_REPORT) return { data: {}, isAbsent: true, isMalformed: false };
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return { data: parsed as DevReportJson, isAbsent: false, isMalformed: false };
    }
    return { data: {}, isAbsent: false, isMalformed: true };
  } catch {
    return { data: {}, isAbsent: false, isMalformed: true };
  }
};

// ---------- ค่า default ของ form ----------
type FormValues = {
  // ข้อมูลการสอน
  teaching_location: string;
  teaching_type: string;
  teaching_date: string;
  teaching_time_slot: string;
  teaching_duration: string;
  // ข้อมูลนักเรียน
  student_id: string;
  student_full_name: string;
  student_nickname: string;
  student_grade_level: string;
  subject: string;
  learning_status: string;
  // หัวข้อ + การบ้าน + คาบหน้า
  topic_learned: string; // = teaching_content ใน DB (sync ทั้ง 2 ตอน submit)
  homework_assignment: string;
  next_class_preparation: string;
  worksheet_drive_link: string;
  // Development report sub-fields (จะถูก JSON.stringify ไปเก็บใน development_report)
  knowledge_rating: string;
  knowledge_behavior: string;
  skills_rating: string;
  skills_behavior: string;
  attitude_rating: string;
  attitude_behavior: string;
  student_improvement: string;
  problems_encountered: string[];
  problems_encountered_other: string;
  solutions_applied: string[];
  solutions_applied_other: string;
  // Fallback ถ้า JSON เก่า parse ไม่ได้
  development_report_raw: string;
  // Admin-only
  status: string;
  rejection_reason: string;
  note: string;
  pinned: boolean;
  // เหตุผลในการแก้ไข (required ทั้ง 2 role)
  reason: string;
};

const buildDefaults = (r: ReportData): FormValues => {
  const { data: dev, isMalformed } = parseDevReport(r.development_report);
  return {
    teaching_location: r.teaching_location ?? '',
    teaching_type: r.teaching_type ?? '',
    teaching_date: r.teaching_date ?? '',
    teaching_time_slot: r.teaching_time_slot ?? '',
    teaching_duration: r.teaching_duration ?? '',
    student_id: r.student_id ?? '',
    student_full_name: r.student_full_name ?? '',
    student_nickname: r.student_nickname ?? '',
    student_grade_level: r.student_grade_level ?? '',
    subject: r.subject ?? '',
    learning_status: r.learning_status ?? '',
    // topic_learned: priority ให้ column teaching_content (top-level) เพราะเป็น truth source หลัก
    topic_learned: r.teaching_content ?? dev.topic_learned ?? '',
    homework_assignment: r.homework_assignment ?? '',
    next_class_preparation: r.next_class_preparation ?? '',
    worksheet_drive_link: r.worksheet_drive_link ?? '',
    knowledge_rating: dev.knowledge_rating ?? '',
    knowledge_behavior: dev.knowledge_behavior ?? '',
    skills_rating: dev.skills_rating ?? '',
    skills_behavior: dev.skills_behavior ?? '',
    attitude_rating: dev.attitude_rating ?? '',
    attitude_behavior: dev.attitude_behavior ?? '',
    student_improvement: dev.student_improvement ?? '',
    problems_encountered: dev.problems_encountered ?? [],
    problems_encountered_other: dev.problems_encountered_other ?? '',
    solutions_applied: dev.solutions_applied ?? [],
    solutions_applied_other: dev.solutions_applied_other ?? '',
    development_report_raw: isMalformed ? (r.development_report ?? '') : '',
    status: r.status ?? 'WAITING',
    rejection_reason: r.rejection_reason ?? '',
    note: r.note ?? '',
    pinned: r.pinned ?? false,
    reason: '',
  };
};

const EditReportPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, checkAdminStatus } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [isRoleChecked, setIsRoleChecked] = useState<boolean>(false);

  // โหลด report
  const { data: report, isLoading } = useQuery({
    queryKey: ['report-edit', id],
    queryFn: async () => {
      if (!id) throw new Error('No report ID');
      const { data, error } = await supabase
        .from('student_development_reports')
        .select('*')
        .eq('id', id)
        .single();
      if (error) throw error;
      return data as ReportData;
    },
  });

  // โหลด PENDING ของ report นี้ (สำหรับ aggregate effective current + auto-cancel ตอน submit)
  const { data: pendings = [], isLoading: pendingsLoading } = useQuery({
    queryKey: ['report-pendings', id],
    queryFn: async () => {
      if (!id) return [] as ReportChangeLog[];
      // ใช้ as any เพราะ types ยังไม่ถูก regenerate หลัง migration ใหม่
      const { data, error } = await (supabase as any)
        .from('report_change_logs')
        .select('*')
        .eq('report_id', id)
        .eq('status', 'PENDING')
        .order('created_at', { ascending: true });
      if (error) {
        // ถ้าตารางยังไม่มี (migration ยังไม่ apply) → ทำงานต่อแบบไม่มี pending
        console.warn('Could not load PENDINGs:', error.message);
        return [] as ReportChangeLog[];
      }
      return (data ?? []) as ReportChangeLog[];
    },
    enabled: !!id,
  });

  // เช็คสิทธิ์ admin
  useEffect(() => {
    const run = async () => {
      if (user) {
        const ok = await checkAdminStatus();
        setIsAdmin(ok);
      }
      setIsRoleChecked(true);
    };
    run();
  }, [user, checkAdminStatus]);

  // เจ้าของ report
  const isOwner = useMemo(
    () => !!user?.email && !!report?.submitter_email && user.email === report.submitter_email,
    [user, report]
  );

  const canEdit = isAdmin || isOwner;

  // Effective current = DB + aggregated PENDING (latest-wins per field)
  // ใช้สำหรับ teacher's prefill + diff baseline (admin ใช้ report ตรงๆ)
  const effectiveCurrent = useMemo<ReportData | null>(() => {
    if (!report) return null;
    if (!pendings || pendings.length === 0) return report;
    const aggregated = aggregatePendingValues(pendings);
    return mergeWithPending(report as unknown as Record<string, unknown>, aggregated) as unknown as ReportData;
  }, [report, pendings]);

  // Prefill source: admin → DB (ตัดสินใจอิสระจาก PENDING), teacher → effective current
  const prefillSource = useMemo<ReportData | null>(() => {
    if (!isRoleChecked) return null;
    return isAdmin ? (report ?? null) : effectiveCurrent;
  }, [isRoleChecked, isAdmin, report, effectiveCurrent]);

  const form = useForm<FormValues>({
    defaultValues: prefillSource ? buildDefaults(prefillSource) : undefined,
  });

  const { register, control, handleSubmit, reset, setValue, formState: { errors } } = form;

  // เฝ้าค่า learning_status เพื่อ toggle ส่วนรายงานพัฒนาการ
  const learningStatus = useWatch({ control, name: 'learning_status' });

  // เฝ้าค่านักเรียนเพื่อแสดงใน StudentEditField (admin เปลี่ยนได้)
  const watchedStudentId = useWatch({ control, name: 'student_id' });
  const watchedStudentFullName = useWatch({ control, name: 'student_full_name' });
  const watchedStudentNickname = useWatch({ control, name: 'student_nickname' });
  const isAbsent = learningStatus === ABSENT_STATUS;
  const isMalformedDevReport = !!report && parseDevReport(report.development_report).isMalformed;

  // Reset form เมื่อ prefill source พร้อม (รอจน report + pendings + role check เสร็จ)
  const [hasInitialized, setHasInitialized] = useState(false);
  useEffect(() => {
    if (hasInitialized) return;
    if (!prefillSource || pendingsLoading) return;
    reset(buildDefaults(prefillSource));
    setHasInitialized(true);
  }, [prefillSource, pendingsLoading, reset, hasInitialized]);

  // ----- Submit -----
  const [submitting, setSubmitting] = useState(false);

  // State สำหรับ confirm dialog ของ admin (เปิดเมื่อมี PENDING overlap)
  const [confirmDialog, setConfirmDialog] = useState<{
    before: Record<string, unknown>;
    after: Record<string, unknown>;
    reason: string;
    overlappingIds: string[];
  } | null>(null);

  // ----- Helper: เรียก RPC ฝั่ง admin (MANUAL)
  const callAdminRPC = useCallback(
    async (
      before: Record<string, unknown>,
      after: Record<string, unknown>,
      reason: string,
      cancelPendingIds: string[] | null
    ) => {
      if (!report) return;
      setSubmitting(true);
      try {
        const { error } = await (supabase as any).rpc('apply_admin_manual_edit', {
          p_report_id: report.id,
          p_before: before,
          p_after: after,
          p_reason: reason,
          p_cancel_pending_ids:
            cancelPendingIds && cancelPendingIds.length > 0 ? cancelPendingIds : null,
        });

        if (error) {
          console.error('apply_admin_manual_edit error:', error);
          toast({
            title: 'เกิดข้อผิดพลาด',
            description: error.message || 'ไม่สามารถบันทึกการแก้ไขได้',
            variant: 'destructive',
          });
          return;
        }

        toast({
          title: 'บันทึกการแก้ไขสำเร็จ',
          description:
            cancelPendingIds && cancelPendingIds.length > 0
              ? `อัปเดตรายงาน + ยกเลิก PENDING ${cancelPendingIds.length} รายการ`
              : 'รายงานถูกอัปเดตและบันทึก log แล้ว',
        });
        navigate(`/reports/${report.id}`, { replace: true });
      } finally {
        setSubmitting(false);
      }
    },
    [report, toast, navigate]
  );

  // ----- Helper: เรียก RPC ฝั่งครู (TEACHER_REQUEST)
  const callTeacherRPC = useCallback(
    async (
      before: Record<string, unknown>,
      after: Record<string, unknown>,
      reason: string,
      supersededPendingIds: string[]
    ) => {
      if (!report) return;
      setSubmitting(true);
      try {
        const { data: logId, error } = await (supabase as any).rpc('apply_teacher_edit_request', {
          p_report_id: report.id,
          p_before: before,
          p_after: after,
          p_reason: reason,
          p_superseded_pending_ids:
            supersededPendingIds.length > 0 ? supersededPendingIds : null,
        });

        if (error) {
          console.error('apply_teacher_edit_request error:', error);
          toast({
            title: 'เกิดข้อผิดพลาด',
            description: error.message || 'ไม่สามารถส่งคำขอแก้ไขได้',
            variant: 'destructive',
          });
          return;
        }

        // Fire-and-forget Telegram notification — never block user on this
        if (logId) {
          void (async () => {
            try {
              const { error: notifyErr } = await supabase.functions.invoke('notify-report-edit-request', {
                body: { log_id: logId, report_id: report.id, site_url: window.location.origin },
              });
              if (notifyErr) {
                console.warn('notify-report-edit-request failed:', notifyErr.message);
              }
            } catch (err) {
              console.warn('notify-report-edit-request error:', err);
            }
          })();
        }

        toast({
          title: 'ส่งคำขอแก้ไขสำเร็จ',
          description:
            supersededPendingIds.length > 0
              ? `บันทึกคำขอใหม่ + ยกเลิกคำขอเก่าที่ถูกแทนที่ ${supersededPendingIds.length} รายการ`
              : 'รอ admin ตรวจสอบและอนุมัติ',
        });
        navigate(`/reports/${report.id}`, { replace: true });
      } finally {
        setSubmitting(false);
      }
    },
    [report, toast, navigate]
  );

  const onSubmit = async (values: FormValues) => {
    if (!report) return;

    // 1) Build development_report JSON จาก sub-fields
    const newDevReport = buildDevelopmentReportJson(values);

    // 2) แมป form values เป็น "DB shape" (key = column ใน student_development_reports)
    // ส่งเฉพาะ student_id — RPC (admin+approve) จะ sync full_name/nickname จาก student.fishsix_students ให้เอง
    const formDbShape: Record<string, unknown> = {
      teaching_location: values.teaching_location,
      teaching_type: values.teaching_type || null,
      teaching_date: values.teaching_date,
      teaching_time_slot: values.teaching_time_slot,
      teaching_duration: values.teaching_duration,
      student_id: values.student_id,
      student_grade_level: values.student_grade_level,
      subject: values.subject,
      learning_status: values.learning_status,
      teaching_content: values.topic_learned,            // topic_learned → teaching_content
      development_report: newDevReport,                  // sub-fields → JSON string
      homework_assignment: values.homework_assignment || null,
      next_class_preparation: values.next_class_preparation || null,
      worksheet_drive_link: values.worksheet_drive_link || null,
    };

    if (isAdmin) {
      formDbShape.status = values.status;
      formDbShape.rejection_reason = values.rejection_reason || null;
      formDbShape.note = values.note || null;
      formDbShape.pinned = values.pinned;
    }

    // 3) Diff baseline ต่างตาม role
    const baseline = isAdmin ? report : (effectiveCurrent ?? report);

    const { before, after, hasChanges } = buildEditDiff(
      formDbShape,
      baseline as unknown as Partial<Record<string, unknown>>
    );

    if (!hasChanges) {
      toast({
        title: 'ไม่มีการเปลี่ยนแปลง',
        description: 'ไม่มีฟิลด์ที่ถูกแก้',
      });
      return;
    }

    // 4) Recompute derived ถ้า teaching_* อย่างใดอย่างหนึ่งเปลี่ยน
    const finalAfter = recomputeDerivedIfNeeded(after, baseline);
    const finalBefore = fillBeforeForDerivedKeys(before, finalAfter, baseline);
    const afterKeys = Object.keys(finalAfter);

    // 5) เส้นทางตาม role
    if (isAdmin) {
      // ตรวจ PENDING overlap → ถ้ามี ให้เปิด dialog ก่อน
      const overlappingIds = findOverlappingPendingIds(pendings, afterKeys);
      if (overlappingIds.length > 0) {
        setConfirmDialog({
          before: finalBefore,
          after: finalAfter,
          reason: values.reason,
          overlappingIds,
        });
        return;
      }
      // ไม่มี overlap → ส่งเลย
      await callAdminRPC(finalBefore, finalAfter, values.reason, null);
    } else {
      // ครู: หา PENDING ที่ถูก supersede แล้ว fire RPC
      const supersededIds = findSupersededPendingIds(pendings, afterKeys);
      await callTeacherRPC(finalBefore, finalAfter, values.reason, supersededIds);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-8 flex justify-center">
        <p className="text-gray-500">กำลังโหลดรายงาน...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="w-full px-4 py-8">
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            ไม่พบรายงาน
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!canEdit) {
    return (
      <div className="w-full px-4 py-8">
        <Card>
          <CardContent className="p-6 text-center text-gray-500">
            คุณไม่มีสิทธิ์แก้ไขรายงานนี้
            <div className="mt-4">
              <Button variant="outline" asChild>
                <Link to={`/reports/${id}`} replace>กลับไปดูรายงาน</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" asChild>
          <Link to={`/reports/${id}`} replace>
            <ArrowLeft className="h-4 w-4 mr-1" />
            ย้อนกลับ
          </Link>
        </Button>
        <h1 className="text-2xl font-bold">แก้ไขรายงาน</h1>
        <Badge variant={isAdmin ? 'default' : 'secondary'}>
          {isAdmin ? 'Admin' : 'ครู (ขอแก้ไข)'}
        </Badge>
      </div>

      <p className="text-sm text-gray-500 mb-6">
        นักเรียน: <span className="font-medium">{report.student_nickname}</span> ({report.student_full_name}) ·
        วันที่สอน: <span className="font-medium">{report.teaching_date}</span>
      </p>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* เหตุผลการแก้ไข - บังคับใส่ทั้ง 2 role */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">เหตุผลในการแก้ไข *</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              {...register('reason', {
                required: 'กรุณาระบุเหตุผลในการแก้ไข',
                minLength: { value: 5, message: 'อย่างน้อย 5 ตัวอักษร' },
              })}
              placeholder="ระบุเหตุผลที่ต้องแก้ไขรายงานนี้"
              rows={3}
            />
            {errors.reason && (
              <p className="text-sm text-red-500 mt-1">{errors.reason.message}</p>
            )}
          </CardContent>
        </Card>

        {/* ข้อมูลการสอน */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ข้อมูลการสอน</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Location */}
            <div>
              <Label className="mb-2 block">สถานที่สอน</Label>
              <Controller
                control={control}
                name="teaching_location"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex flex-wrap gap-4"
                  >
                    {LOCATION_OPTIONS.map((o) => (
                      <div key={o.value} className="flex items-center gap-2">
                        <RadioGroupItem value={o.value} id={`loc-${o.value}`} />
                        <Label htmlFor={`loc-${o.value}`}>{o.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
            </div>

            {/* Teaching type */}
            <div>
              <Label className="mb-2 block">ประเภทการสอน</Label>
              <Controller
                control={control}
                name="teaching_type"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="เลือกประเภทการสอน" /></SelectTrigger>
                    <SelectContent>
                      {TEACHING_TYPE_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Date + Time */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">วันที่สอน</Label>
                <Input type="date" {...register('teaching_date', { required: true })} />
              </div>
              <div>
                <Label className="mb-2 block">เวลาเริ่มสอน</Label>
                <Input type="time" {...register('teaching_time_slot', { required: true })} />
              </div>
            </div>

            {/* Duration */}
            <div>
              <Label className="mb-2 block">ระยะเวลาการสอน</Label>
              <Controller
                control={control}
                name="teaching_duration"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex flex-wrap gap-4"
                  >
                    {DURATION_OPTIONS.map((o) => (
                      <div key={o.value} className="flex items-center gap-2">
                        <RadioGroupItem value={o.value} id={`dur-${o.value}`} />
                        <Label htmlFor={`dur-${o.value}`}>{o.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
              <p className="text-xs text-gray-500 mt-1">
                หมายเหตุ: `class_duration`, `start_date`, `end_date`, `submitted_on_time` จะคำนวณใหม่อัตโนมัติตอน submit
              </p>
            </div>
          </CardContent>
        </Card>

        {/* ข้อมูลนักเรียน */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ข้อมูลนักเรียน</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <StudentEditField
              mode={isAdmin ? 'admin' : 'teacher'}
              currentValues={{
                student_id: watchedStudentId ?? '',
                student_full_name: watchedStudentFullName ?? '',
                student_nickname: watchedStudentNickname ?? '',
              }}
              original={{
                student_id: report.student_id,
                student_full_name: report.student_full_name,
                student_nickname: report.student_nickname,
              }}
              onChange={(next) => {
                setValue('student_id', next.student_id, { shouldDirty: true });
                setValue('student_full_name', next.student_full_name, { shouldDirty: true });
                setValue('student_nickname', next.student_nickname, { shouldDirty: true });
              }}
            />

            {/* Grade */}
            <div>
              <Label className="mb-2 block">ระดับชั้น</Label>
              <Controller
                control={control}
                name="student_grade_level"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger><SelectValue placeholder="เลือกระดับชั้น" /></SelectTrigger>
                    <SelectContent>
                      {GRADE_OPTIONS.map((g) => (
                        <SelectItem key={g} value={g}>{g}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            {/* Subject */}
            <div>
              <Label className="mb-2 block">วิชาที่สอน</Label>
              <Controller
                control={control}
                name="subject"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="grid grid-cols-2 md:grid-cols-3 gap-2"
                  >
                    {SUBJECT_OPTIONS.map((s) => (
                      <div key={s} className="flex items-center gap-2">
                        <RadioGroupItem value={s} id={`subj-${s}`} />
                        <Label htmlFor={`subj-${s}`}>{s}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
            </div>

            {/* Learning status */}
            <div>
              <Label className="mb-2 block">สถานะการเรียน</Label>
              <Controller
                control={control}
                name="learning_status"
                render={({ field }) => (
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    className="flex flex-col gap-2"
                  >
                    {LEARNING_STATUS_OPTIONS.map((o) => (
                      <div key={o.value} className="flex items-center gap-2">
                        <RadioGroupItem value={o.value} id={`ls-${o.value}`} />
                        <Label htmlFor={`ls-${o.value}`}>{o.label}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* เนื้อหาการสอน */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">เนื้อหาการสอน</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <Label className="mb-2 block">หัวข้อ/เนื้อหาที่สอน</Label>
              <Input {...register('topic_learned')} />
              <p className="text-xs text-gray-500 mt-1">
                * ค่านี้จะ sync ทั้ง column `teaching_content` และฟิลด์ `topic_learned` ใน development_report
              </p>
            </div>
            <div>
              <Label className="mb-2 block">การบ้านที่มอบหมาย</Label>
              <Textarea rows={3} {...register('homework_assignment')} />
            </div>
            <div>
              <Label className="mb-2 block">เตรียมเรียนคาบหน้า</Label>
              <Textarea rows={3} {...register('next_class_preparation')} />
            </div>
            <div>
              <Label className="mb-2 block">ลิงก์ Google Drive ใบงาน</Label>
              <Input type="url" {...register('worksheet_drive_link')} />
            </div>
          </CardContent>
        </Card>

        {/* รายงานพัฒนาการ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">รายงานพัฒนาการ</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {isAbsent ? (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-700">
                สถานะการเรียน = <strong>"น้องไม่เข้าเรียน (ลากะทันหัน)"</strong> →
                รายงานพัฒนาการจะถูกเก็บเป็น <code>"นักเรียนไม่เข้าเรียน"</code> โดยอัตโนมัติ
                ไม่ต้องกรอกฟิลด์ด้านล่าง
              </div>
            ) : (
              <>
                {isMalformedDevReport && (
                  <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    ⚠️ รายงานพัฒนาการเดิมไม่ใช่ JSON ที่ valid (อาจเป็น data import จาก Google Sheet)
                    ฟิลด์ด้านล่างจะว่างเปล่า — ค่าเก่าก่อนแก้ดูได้ใน raw ด้านล่างสุด
                  </div>
                )}

                {/* 3 sections: knowledge / skills / attitude */}
                {[
                  { key: 'knowledge', label: 'ด้านความรู้ (Knowledge)' },
                  { key: 'skills', label: 'ด้านทักษะ (Skills)' },
                  { key: 'attitude', label: 'ด้านทัศนคติ (Attitude)' },
                ].map(({ key, label }) => (
                  <div key={key} className="space-y-3 border-l-4 border-blue-200 pl-4">
                    <h4 className="font-medium text-sm">{label}</h4>
                    <div>
                      <Label className="mb-2 block text-sm">คะแนน (1-5)</Label>
                      <Controller
                        control={control}
                        name={`${key}_rating` as keyof FormValues as 'knowledge_rating'}
                        render={({ field }) => (
                          <RadioGroup
                            value={field.value as string}
                            onValueChange={field.onChange}
                            className="flex flex-wrap gap-3"
                          >
                            {RATING_OPTIONS.map((o) => (
                              <div key={o.value} className="flex items-center gap-2">
                                <RadioGroupItem value={o.value} id={`${key}-r-${o.value}`} />
                                <Label htmlFor={`${key}-r-${o.value}`} className="text-sm">{o.label}</Label>
                              </div>
                            ))}
                          </RadioGroup>
                        )}
                      />
                    </div>
                    <div>
                      <Label className="mb-2 block text-sm">พฤติกรรมที่สังเกตได้</Label>
                      <Textarea
                        rows={2}
                        {...register(`${key}_behavior` as keyof FormValues as 'knowledge_behavior')}
                      />
                    </div>
                  </div>
                ))}

                {/* Improvement */}
                <div>
                  <Label className="mb-2 block">พัฒนาการของน้อง</Label>
                  <Textarea rows={3} {...register('student_improvement')} />
                </div>

                {/* Problems encountered (checkbox) */}
                <div>
                  <Label className="mb-2 block">ปัญหาที่พบ (เลือกได้หลายข้อ)</Label>
                  <Controller
                    control={control}
                    name="problems_encountered"
                    render={({ field }) => {
                      const value = (field.value ?? []) as string[];
                      const toggle = (v: string) => {
                        if (value.includes(v)) field.onChange(value.filter((x) => x !== v));
                        else field.onChange([...value, v]);
                      };
                      return (
                        <div className="space-y-2">
                          {PROBLEM_OPTIONS.map((opt) => (
                            <div key={opt} className="flex items-start gap-2">
                              <Checkbox
                                id={`pb-${opt}`}
                                checked={value.includes(opt)}
                                onCheckedChange={() => toggle(opt)}
                              />
                              <Label htmlFor={`pb-${opt}`} className="text-sm font-normal leading-tight">{opt}</Label>
                            </div>
                          ))}
                          <div className="flex items-start gap-2">
                            <Checkbox
                              id="pb-other"
                              checked={value.includes('Other')}
                              onCheckedChange={() => toggle('Other')}
                            />
                            <Label htmlFor="pb-other" className="text-sm font-normal leading-tight">อื่นๆ</Label>
                          </div>
                          {value.includes('Other') && (
                            <Input
                              className="mt-2"
                              placeholder="ระบุปัญหาอื่นๆ"
                              {...register('problems_encountered_other')}
                            />
                          )}
                        </div>
                      );
                    }}
                  />
                </div>

                {/* Solutions applied (checkbox) */}
                <div>
                  <Label className="mb-2 block">วิธีแก้ที่ใช้ (เลือกได้หลายข้อ)</Label>
                  <Controller
                    control={control}
                    name="solutions_applied"
                    render={({ field }) => {
                      const value = (field.value ?? []) as string[];
                      const toggle = (v: string) => {
                        if (value.includes(v)) field.onChange(value.filter((x) => x !== v));
                        else field.onChange([...value, v]);
                      };
                      return (
                        <div className="space-y-2">
                          {SOLUTION_OPTIONS.map((opt) => (
                            <div key={opt} className="flex items-start gap-2">
                              <Checkbox
                                id={`sl-${opt}`}
                                checked={value.includes(opt)}
                                onCheckedChange={() => toggle(opt)}
                              />
                              <Label htmlFor={`sl-${opt}`} className="text-sm font-normal leading-tight">{opt}</Label>
                            </div>
                          ))}
                          <div className="flex items-start gap-2">
                            <Checkbox
                              id="sl-other"
                              checked={value.includes('Other')}
                              onCheckedChange={() => toggle('Other')}
                            />
                            <Label htmlFor="sl-other" className="text-sm font-normal leading-tight">อื่นๆ</Label>
                          </div>
                          {value.includes('Other') && (
                            <Input
                              className="mt-2"
                              placeholder="ระบุวิธีแก้อื่นๆ"
                              {...register('solutions_applied_other')}
                            />
                          )}
                        </div>
                      );
                    }}
                  />
                </div>

                {/* Raw fallback (เฉพาะกรณี malformed) */}
                {isMalformedDevReport && (
                  <div>
                    <Label className="mb-2 block text-sm text-gray-600">
                      ค่าเดิมก่อนแก้ (read-only — เก็บไว้ดูเฉยๆ)
                    </Label>
                    <Textarea
                      rows={4}
                      readOnly
                      className="bg-gray-50 text-xs font-mono"
                      {...register('development_report_raw')}
                    />
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* ไฟล์ (preview-only ในเฟสนี้) */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">ไฟล์ (รูปเช็คอิน / ใบงาน)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <Label className="mb-1 block">รูปเช็คอิน ({report.check_in_photo_urls?.length ?? 0} ไฟล์)</Label>
              {report.check_in_photo_urls?.length ? (
                <ul className="list-disc list-inside text-xs text-blue-600">
                  {report.check_in_photo_urls.map((u, i) => (
                    <li key={i}><a href={u} target="_blank" rel="noreferrer" className="underline break-all">{u}</a></li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-xs">ไม่มีไฟล์</p>
              )}
            </div>
            <div>
              <Label className="mb-1 block">ใบงาน ({report.worksheet_urls?.length ?? 0} ไฟล์)</Label>
              {report.worksheet_urls?.length ? (
                <ul className="list-disc list-inside text-xs text-blue-600">
                  {report.worksheet_urls.map((u, i) => (
                    <li key={i}><a href={u} target="_blank" rel="noreferrer" className="underline break-all">{u}</a></li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500 text-xs">ไม่มีไฟล์</p>
              )}
            </div>
            <p className="text-xs text-amber-600">
              การอัปโหลด/ลบไฟล์จะ implement ในเฟสถัดไป (ต้องพึ่ง schema log ที่จะออกแบบใหม่)
            </p>
          </CardContent>
        </Card>

        {/* Admin-only */}
        {isAdmin && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader>
              <CardTitle className="text-base">สำหรับ Admin เท่านั้น</CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              <div>
                <Label className="mb-2 block">สถานะรายงาน</Label>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((o) => (
                          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>
              <div>
                <Label className="mb-2 block">เหตุผลที่ไม่อนุมัติ (ถ้ามี)</Label>
                <Textarea rows={2} {...register('rejection_reason')} />
              </div>
              <div>
                <Label className="mb-2 block">หมายเหตุ (note)</Label>
                <Textarea rows={2} {...register('note')} />
              </div>
              <div className="flex items-center gap-2">
                <Controller
                  control={control}
                  name="pinned"
                  render={({ field }) => (
                    <Checkbox
                      id="pinned"
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(!!v)}
                    />
                  )}
                />
                <Label htmlFor="pinned">ปักหมุดรายงานนี้</Label>
              </div>
            </CardContent>
          </Card>
        )}

        <Separator />

        {/* Actions */}
        <div className="flex flex-col-reverse md:flex-row gap-3 md:justify-end">
          <Button type="button" variant="outline" asChild disabled={submitting}>
            <Link to={`/reports/${id}`} replace>ยกเลิก</Link>
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting
              ? 'กำลังบันทึก...'
              : isAdmin
                ? 'บันทึกการแก้ไข'
                : 'ส่งคำขอแก้ไข'}
          </Button>
        </div>
      </form>

      {/* Admin overlap confirm dialog */}
      <AlertDialog
        open={!!confirmDialog}
        onOpenChange={(open) => {
          if (!open) setConfirmDialog(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>พบคำขอแก้ไขของครูที่ทับซ้อน</AlertDialogTitle>
            <AlertDialogDescription>
              มี PENDING ของครู{' '}
              <strong>{confirmDialog?.overlappingIds.length ?? 0} รายการ</strong>{' '}
              ที่มีฟิลด์ทับซ้อนกับการแก้ไขของคุณ — เลือกวิธีดำเนินการ
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button
              variant="default"
              disabled={submitting}
              onClick={async () => {
                if (!confirmDialog) return;
                const data = confirmDialog;
                setConfirmDialog(null);
                await callAdminRPC(
                  data.before,
                  data.after,
                  data.reason,
                  data.overlappingIds
                );
              }}
            >
              ดำเนินการต่อ + ยกเลิก PENDING ที่ทับซ้อน
            </Button>
            <Button
              variant="secondary"
              disabled={submitting}
              onClick={async () => {
                if (!confirmDialog) return;
                const data = confirmDialog;
                setConfirmDialog(null);
                await callAdminRPC(data.before, data.after, data.reason, null);
              }}
            >
              ดำเนินการต่อ (เก็บ PENDING ไว้ตามเดิม)
            </Button>
            <Button
              variant="outline"
              disabled={submitting}
              onClick={() => setConfirmDialog(null)}
            >
              ยกเลิก กลับไปแก้
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default EditReportPage;
